import {
  NPC_KIND_AVATAR_CLASS,
  behaviourTag,
  isTripletExpected,
  npcExpectedLabel,
  npcInitial,
  npcMomentLine,
  npcSubtitle,
} from '@/lib/npc-labels';
import { noyauPillInkClass } from '@/lib/session-noyau';
import { cn } from '@/lib/utils';
import type { NoyauCode, NpcCard, NpcExpectedNoyau, NpcKind } from '@matter_extractor/shared';
import { noyauOf } from '@matter_extractor/shared';
import { type Noyau, NoyauBadge, Tooltip } from '@pms-digital/design-system';

/**
 * The blocks an NPC's card adds to the common tile (npc.md §12; maquette `Bibliotheque`, planche
 * « a. L'onglet NPC »).
 *
 * The card is the persona's pattern - an avatar, the operator's name, a subtitle - read off the
 * development version (an NPC has no other side, N5), plus what an operator scans a library of
 * stand-ins for: the moment, the behaviours it puts to the test, and the noyau it is expected
 * to give. Four pieces, each in one of the layer's slots:
 *
 *   - `NpcCardIdentity` - beside the chevron;
 *   - `NpcCardExtra` - the moment and the behaviour tags, between that row and the pastilles;
 *   - `NpcCardPills` - « noyau attendu SGP », leading the row of the version's pastille;
 *   - the line « Joué par 12 rejeux » is the layer's own facts line (`facetsOf`).
 *
 * The avatar is `aria-hidden`: it restates the subtitle, which already says « Célèbre · Marie ».
 */

/** The round avatar: the first name's initial, coloured by the kind (§12). */
export function NpcAvatar({
  firstName,
  kind,
  size = 'md',
}: {
  firstName: string;
  kind: NpcKind;
  size?: 'sm' | 'md' | 'lg';
}) {
  const box =
    size === 'lg'
      ? 'size-13 text-h4'
      : size === 'sm'
        ? 'size-7 text-caption'
        : 'size-11 text-body-lg';
  return (
    <span
      aria-hidden="true"
      data-kind={kind}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-bold leading-none',
        box,
        NPC_KIND_AVATAR_CLASS[kind],
      )}
    >
      {npcInitial(firstName)}
    </span>
  );
}

export function NpcCardIdentity({ card }: { card: NpcCard }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-3">
      <NpcAvatar firstName={card.firstName} kind={card.kind} />
      <div className="flex min-w-0 flex-col gap-0.5">
        <h2 className="min-w-0 break-words font-semibold text-bleu-nuit text-body-lg leading-7">
          {card.name}
        </h2>
        <p data-testid="npc-sous-titre" className="min-w-0 break-words text-caption text-gray-600">
          {npcSubtitle(card)}
        </p>
      </div>
    </div>
  );
}

/** The moment, then the behaviours as quiet tags. Renders nothing when there is neither. */
export function NpcCardExtra({ card }: { card: NpcCard }) {
  if (card.moment === '' && card.behaviours.length === 0) return null;
  return (
    <div className="space-y-2">
      {card.moment === '' ? null : (
        <p data-testid="npc-moment" className="text-gray-800 text-small">
          {npcMomentLine(card.moment)}
        </p>
      )}
      {card.behaviours.length === 0 ? null : (
        <ul className="flex flex-wrap gap-1.5">
          {card.behaviours.map((behaviour) => (
            <li
              key={behaviour}
              data-testid="npc-etiquette"
              className="rounded-full border border-gray-300 bg-white px-2.5 py-0.5 font-semibold text-caption text-gray-700 leading-4"
            >
              {behaviourTag(behaviour)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The charte's name of each noyau: the package names its noyaux by their colour, the base by their
 * triplet. Written as a table rather than looked up, so a code the package did not know would fail
 * the type check instead of falling back on some colour.
 */
const CHARTE_NOYAU: Readonly<Record<NoyauCode, Noyau>> = {
  SGP: 'bleu-nuit',
  SPG: 'bleu-ciel',
  GSP: 'rose',
  GPS: 'rouge',
  PGS: 'jaune',
  PSG: 'vert',
};

/**
 * The expected noyau as a pill: a triplet is the package `NoyauBadge` - the noyau's own colour and
 * its ink, the triplet as its label -, « hors base » is an outline, and none is a sentence. Never
 * compared on a screen to the noyau a session retained (N10).
 */
export function NpcNoyauPill({
  expected,
  size = 'micro',
}: {
  expected: NpcExpectedNoyau;
  /** The text: 11px on the cards, the 13px of the replay tables aligned on the design system (#216). */
  size?: 'micro' | 'caption';
}) {
  const text = size === 'caption' ? 'text-caption' : 'text-micro';
  if (!isTripletExpected(expected)) {
    return (
      <span
        data-testid="npc-noyau-attendu"
        data-noyau={expected}
        className={cn(
          'inline-flex h-5 items-center rounded-full border border-gray-400 bg-white px-2 font-bold text-gray-700 leading-none',
          text,
        )}
      >
        {npcExpectedLabel(expected)}
      </span>
    );
  }
  const noyau = noyauOf(expected);
  return (
    <Tooltip content={noyau.name}>
      {size === 'caption' ? (
        // The package's `NoyauBadge` has one size, 12px: the 13px of the replay tables keeps
        // the pill of #216, the noyau's own colour and ink, until the package offers a size.
        <span
          data-testid="npc-noyau-attendu"
          data-noyau={expected}
          className={cn(
            'inline-flex h-5 min-w-8 items-center justify-center rounded-full px-2 font-bold leading-none tracking-wide',
            text,
            noyauPillInkClass(expected),
          )}
          style={{ backgroundColor: noyau.color }}
        >
          {expected}
        </span>
      ) : (
        <span data-testid="npc-noyau-attendu" data-noyau={expected} className="inline-flex">
          <NoyauBadge noyau={CHARTE_NOYAU[expected]}>{expected}</NoyauBadge>
        </span>
      )}
    </Tooltip>
  );
}

export function NpcCardPills({ card }: { card: NpcCard }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-caption text-gray-600 leading-4">
      {card.expectedNoyau === null ? (
        <span data-testid="npc-sans-noyau">{npcExpectedLabel(null)}</span>
      ) : (
        <>
          noyau attendu
          <NpcNoyauPill expected={card.expectedNoyau} />
        </>
      )}
    </span>
  );
}
